import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Plus, Edit2, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '../../components/ui/Button';
import Modal from '../../components/Modal';
import Input from '../../components/ui/Input';
import TextArea from '../../components/ui/Textarea';
import { useToast } from '../../components/Toast';
import estadosOSService from '../../services/estadosOS';
const EstadosOSPage = () => {
    const { success, error } = useToast();
    const queryClient = useQueryClient();
    const [showModal, setShowModal] = useState(false);
    const [editingEstado, setEditingEstado] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    // Form state
    const [formData, setFormData] = useState({
        nome: '',
        descricao: '',
        cor: '#3B82F6',
        ordem: 0,
        ativo: true,
        podeEditar: true,
        podeExcluir: true,
        notificarCliente: false,
        notificarTecnico: false,
        estadoInicial: false,
        estadoFinal: false
    });
    // Query para buscar estados
    const { data: estados = [], isLoading } = useQuery({
        queryKey: ['estados-os'],
        queryFn: () => estadosOSService.list()
    });
    // Mutations
    const createMutation = useMutation({
        mutationFn: (data) => estadosOSService.create(data),
        onSuccess: () => {
            success('Estado criado com sucesso');
            queryClient.invalidateQueries({ queryKey: ['estados-os'] });
            handleCloseModal();
        },
        onError: (err) => {
            error('Erro ao criar estado', err.message);
        }
    });
    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => estadosOSService.update(id, data),
        onSuccess: () => {
            success('Estado atualizado com sucesso');
            queryClient.invalidateQueries({ queryKey: ['estados-os'] });
            handleCloseModal();
        },
        onError: (err) => {
            error('Erro ao atualizar estado', err.message);
        }
    });
    const deleteMutation = useMutation({
        mutationFn: (id) => estadosOSService.delete(id),
        onSuccess: () => {
            success('Estado excluído com sucesso');
            queryClient.invalidateQueries({ queryKey: ['estados-os'] });
            setShowDeleteConfirm(null);
        },
        onError: (err) => {
            error('Erro ao excluir estado', err.message);
        }
    });
    const reorderMutation = useMutation({
        mutationFn: (estados) => estadosOSService.reorder(estados),
        onSuccess: () => {
            success('Ordem atualizada com sucesso');
            queryClient.invalidateQueries({ queryKey: ['estados-os'] });
        },
        onError: (err) => {
            error('Erro ao reordenar estados', err.message);
        }
    });
    const handleOpenModal = (estado) => {
        if (estado) {
            setEditingEstado(estado);
            setFormData({
                nome: estado.nome,
                descricao: estado.descricao || '',
                cor: estado.cor,
                ordem: estado.ordem,
                ativo: estado.ativo,
                podeEditar: estado.podeEditar,
                podeExcluir: estado.podeExcluir,
                notificarCliente: estado.notificarCliente,
                notificarTecnico: estado.notificarTecnico,
                estadoInicial: estado.estadoInicial || false,
                estadoFinal: estado.estadoFinal || false
            });
        }
        else {
            setEditingEstado(null);
            const maxOrdem = Math.max(...estados.map(e => e.ordem), 0);
            setFormData({
                nome: '',
                descricao: '',
                cor: '#3B82F6',
                ordem: maxOrdem + 1,
                ativo: true,
                podeEditar: true,
                podeExcluir: true,
                notificarCliente: false,
                notificarTecnico: false,
                estadoInicial: false,
                estadoFinal: false
            });
        }
        setShowModal(true);
    };
    const handleCloseModal = () => {
        setShowModal(false);
        setEditingEstado(null);
        setFormData({
            nome: '',
            descricao: '',
            cor: '#3B82F6',
            ordem: 0,
            ativo: true,
            podeEditar: true,
            podeExcluir: true,
            notificarCliente: false,
            notificarTecnico: false,
            estadoInicial: false,
            estadoFinal: false
        });
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.nome) {
            error('Nome é obrigatório');
            return;
        }
        if (editingEstado) {
            updateMutation.mutate({ id: editingEstado.id, data: formData });
        }
        else {
            createMutation.mutate(formData);
        }
    };
    const handleMoveUp = (index) => {
        if (index === 0)
            return;
        const newEstados = [...estados];
        const temp = newEstados[index].ordem;
        newEstados[index].ordem = newEstados[index - 1].ordem;
        newEstados[index - 1].ordem = temp;
        const updates = [
            { id: newEstados[index].id, ordem: newEstados[index].ordem },
            { id: newEstados[index - 1].id, ordem: newEstados[index - 1].ordem }
        ];
        reorderMutation.mutate(updates);
    };
    const handleMoveDown = (index) => {
        if (index === estados.length - 1)
            return;
        const newEstados = [...estados];
        const temp = newEstados[index].ordem;
        newEstados[index].ordem = newEstados[index + 1].ordem;
        newEstados[index + 1].ordem = temp;
        const updates = [
            { id: newEstados[index].id, ordem: newEstados[index].ordem },
            { id: newEstados[index + 1].id, ordem: newEstados[index + 1].ordem }
        ];
        reorderMutation.mutate(updates);
    };
    const predefinedColors = [
        { name: 'Azul', value: '#3B82F6' },
        { name: 'Verde', value: '#10B981' },
        { name: 'Amarelo', value: '#F59E0B' },
        { name: 'Vermelho', value: '#EF4444' },
        { name: 'Roxo', value: '#8B5CF6' },
        { name: 'Rosa', value: '#EC4899' },
        { name: 'Índigo', value: '#6366F1' },
        { name: 'Cinza', value: '#6B7280' }
    ];
    if (isLoading) {
        return (_jsx("div", { className: "flex items-center justify-center min-h-[400px]", children: _jsx("div", { className: "animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" }) }));
    }
    return (_jsxs("div", { className: "space-y-6", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-2xl font-bold text-gray-900", children: "Estados de Ordens de Servi\u00E7o" }), _jsx("p", { className: "text-gray-600 mt-1", children: "Configure os estados dispon\u00EDveis para as ordens de servi\u00E7o" })] }), _jsx(Button, { onClick: () => handleOpenModal(), leftIcon: _jsx(Plus, { className: "h-4 w-4" }), children: "Novo Estado" })] }), _jsx("div", { className: "bg-white rounded-lg shadow", children: estados.length === 0 ? (_jsx("div", { className: "p-8 text-center", children: _jsx("p", { className: "text-gray-500", children: "Nenhum estado cadastrado" }) })) : (_jsx("div", { className: "divide-y divide-gray-200", children: estados
                        .sort((a, b) => a.ordem - b.ordem)
                        .map((estado, index) => (_jsx("div", { className: "p-4 hover:bg-gray-50 transition-colors", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center space-x-4", children: [_jsx("div", { className: "w-8 h-8 rounded-full border-2 border-gray-300", style: { backgroundColor: estado.cor } }), _jsxs("div", { children: [_jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("h3", { className: "text-sm font-medium text-gray-900", children: estado.nome }), !estado.ativo && (_jsx("span", { className: "px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded", children: "Inativo" })), estado.estadoInicial && (_jsx("span", { className: "px-2 py-1 text-xs bg-green-100 text-green-700 rounded", children: "Inicial" })), estado.estadoFinal && (_jsx("span", { className: "px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded", children: "Final" }))] }), estado.descricao && (_jsx("p", { className: "text-sm text-gray-500 mt-1", children: estado.descricao })), _jsxs("div", { className: "flex items-center space-x-4 text-xs text-gray-400 mt-1", children: [estado.notificarCliente && (_jsx("span", { children: "Notifica cliente" })), estado.notificarTecnico && (_jsx("span", { children: "Notifica t\u00E9cnico" }))] })] })] }), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(Button, { variant: "ghost", size: "sm", onClick: () => handleMoveUp(index), disabled: index === 0, children: _jsx(ArrowUp, { className: "h-4 w-4" }) }), _jsx(Button, { variant: "ghost", size: "sm", onClick: () => handleMoveDown(index), disabled: index === estados.length - 1, children: _jsx(ArrowDown, { className: "h-4 w-4" }) }), _jsx(Button, { variant: "ghost", size: "sm", onClick: () => handleOpenModal(estado), children: _jsx(Edit2, { className: "h-4 w-4" }) }), estado.podeExcluir && (_jsx(Button, { variant: "ghost", size: "sm", onClick: () => setShowDeleteConfirm(estado.id), children: _jsx(Trash2, { className: "h-4 w-4 text-red-500" }) }))] })] }) }, estado.id))) })) }), _jsx(Modal, { isOpen: showModal, onClose: handleCloseModal, title: editingEstado ? 'Editar Estado' : 'Novo Estado', size: "lg", children: _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Nome *" }), _jsx(Input, { value: formData.nome, onChange: (e) => setFormData({ ...formData, nome: e.target.value }), placeholder: "Ex: Em An\u00E1lise", required: true })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Ordem" }), _jsx(Input, { type: "number", value: formData.ordem, onChange: (e) => setFormData({ ...formData, ordem: parseInt(e.target.value) }), min: 0 })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Descri\u00E7\u00E3o" }), _jsx(TextArea, { value: formData.descricao, onChange: (e) => setFormData({ ...formData, descricao: e.target.value }), placeholder: "Descri\u00E7\u00E3o do estado", rows: 3 })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "Cor" }), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("input", { type: "color", value: formData.cor, onChange: (e) => setFormData({ ...formData, cor: e.target.value }), className: "h-10 w-20" }), _jsx("div", { className: "flex space-x-1", children: predefinedColors.map((color) => (_jsx("button", { type: "button", onClick: () => setFormData({ ...formData, cor: color.value }), className: "w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-400", style: { backgroundColor: color.value }, title: color.name }, color.value))) })] })] }), _jsxs("div", { className: "space-y-2", children: [_jsxs("label", { className: "flex items-center space-x-2", children: [_jsx("input", { type: "checkbox", checked: formData.ativo, onChange: (e) => setFormData({ ...formData, ativo: e.target.checked }), className: "rounded border-gray-300" }), _jsx("span", { className: "text-sm text-gray-700", children: "Ativo" })] }), _jsxs("label", { className: "flex items-center space-x-2", children: [_jsx("input", { type: "checkbox", checked: formData.estadoInicial, onChange: (e) => setFormData({ ...formData, estadoInicial: e.target.checked }), className: "rounded border-gray-300" }), _jsx("span", { className: "text-sm text-gray-700", children: "Estado inicial (novo OS)" })] }), _jsxs("label", { className: "flex items-center space-x-2", children: [_jsx("input", { type: "checkbox", checked: formData.estadoFinal, onChange: (e) => setFormData({ ...formData, estadoFinal: e.target.checked }), className: "rounded border-gray-300" }), _jsx("span", { className: "text-sm text-gray-700", children: "Estado final (conclus\u00E3o)" })] }), _jsxs("label", { className: "flex items-center space-x-2", children: [_jsx("input", { type: "checkbox", checked: formData.notificarCliente, onChange: (e) => setFormData({ ...formData, notificarCliente: e.target.checked }), className: "rounded border-gray-300" }), _jsx("span", { className: "text-sm text-gray-700", children: "Notificar cliente ao mudar para este estado" })] }), _jsxs("label", { className: "flex items-center space-x-2", children: [_jsx("input", { type: "checkbox", checked: formData.notificarTecnico, onChange: (e) => setFormData({ ...formData, notificarTecnico: e.target.checked }), className: "rounded border-gray-300" }), _jsx("span", { className: "text-sm text-gray-700", children: "Notificar t\u00E9cnico ao mudar para este estado" })] }), _jsxs("label", { className: "flex items-center space-x-2", children: [_jsx("input", { type: "checkbox", checked: formData.podeEditar, onChange: (e) => setFormData({ ...formData, podeEditar: e.target.checked }), className: "rounded border-gray-300" }), _jsx("span", { className: "text-sm text-gray-700", children: "Permite edi\u00E7\u00E3o" })] }), _jsxs("label", { className: "flex items-center space-x-2", children: [_jsx("input", { type: "checkbox", checked: formData.podeExcluir, onChange: (e) => setFormData({ ...formData, podeExcluir: e.target.checked }), className: "rounded border-gray-300" }), _jsx("span", { className: "text-sm text-gray-700", children: "Permite exclus\u00E3o" })] })] }), _jsxs("div", { className: "flex justify-end space-x-3 pt-4", children: [_jsx(Button, { type: "button", variant: "outline", onClick: handleCloseModal, children: "Cancelar" }), _jsxs(Button, { type: "submit", isLoading: createMutation.isPending || updateMutation.isPending, children: [editingEstado ? 'Atualizar' : 'Criar', " Estado"] })] })] }) }), _jsx(Modal, { isOpen: showDeleteConfirm !== null, onClose: () => setShowDeleteConfirm(null), title: "Confirmar Exclus\u00E3o", size: "sm", children: _jsxs("div", { className: "space-y-4", children: [_jsx("p", { className: "text-gray-600", children: "Tem certeza que deseja excluir este estado?" }), _jsx("p", { className: "text-sm text-red-600", children: "Esta a\u00E7\u00E3o n\u00E3o pode ser desfeita e pode afetar ordens de servi\u00E7o existentes." }), _jsxs("div", { className: "flex justify-end space-x-3 pt-4", children: [_jsx(Button, { variant: "outline", onClick: () => setShowDeleteConfirm(null), children: "Cancelar" }), _jsx(Button, { variant: "danger", onClick: () => {
                                        if (showDeleteConfirm !== null) {
                                            deleteMutation.mutate(showDeleteConfirm);
                                        }
                                    }, isLoading: deleteMutation.isPending, children: "Excluir" })] })] }) })] }));
};
export default EstadosOSPage;
